import React, { useEffect, useState } from 'react';
import MessageBox from '../MessageBox/MessageBox';
import {
  formatDateStringToDBDate,
  formatDateStringToFrenchDate,
  isValidDate,
  isDateBeforeToday,
  isDateAfterToday,
} from '../../utils/dateUtils';

export default function DateInput({
  name,
  value,
  onChange,
  disablePastDateWarning = false,
  disableFutureDates = false,
}) {
  const [rawValue, setRawValue] = useState('');
  const [warnedValue, setWarnedValue] = useState(null);
  const placeholder = new Date()
    .toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    .replace(/\//g, '');
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [messageBoxConfig, setMessageBoxConfig] = useState({
    message: '',
    type: 'ok',
    onConfirm: null,
    onCancel: () => setShowMessageBox(false),
    onClose: () => setShowMessageBox(false),
    loading_button: false,
    input: false,
  });

  useEffect(() => {
    if (value) {
      setRawValue(formatDateStringToFrenchDate(value));
    }
    setWarnedValue(null);
  }, [value]);

  const showPastDateWarning = () => {
    setMessageBoxConfig({
      message: "Attention : la date saisie est antérieure à aujourd'hui.",
      type: 'ok',
      loading_button: false,
      input: false,
      onConfirm: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  };

  const handleChange = e => {
    setRawValue(e.target.value);
    if (isValidDate(e.target.value)) {
      e.target.value = formatDateStringToDBDate(e.target.value);
      onChange(e || '');
    }
  };

  const handleBlur = e => {
    const { value } = e.target;
    const today = new Date();
    const year =
      today.getDate() >= 28 && today.getMonth() === 11
        ? today.getFullYear() + 1
        : today.getFullYear();
    if (!value) {
      e.target.value = '';
      setRawValue('');
      onChange(e);
    } else {
      let finalDate = null;
      if (value.length === 3 && value.slice(0, 1) < 10 && value.slice(-2) <= 12) {
        finalDate = `${year}-${value.slice(-2)}-0${value.slice(0, 1)}`;
        e.target.value = finalDate;
        setRawValue(formatDateStringToFrenchDate(e.target.value));
        onChange(e);
      } else if (value.length === 4 && value.slice(0, 2) <= 31 && value.slice(-2) <= 12) {
        finalDate = `${year}-${value.slice(-2)}-${value.slice(0, 2)}`;
        e.target.value = finalDate;
        setRawValue(formatDateStringToFrenchDate(e.target.value));
        onChange(e);
      } else if (
        (value.length === 5 || value.length === 7) &&
        value.slice(0, 1) < 10 &&
        value.slice(1, 3) <= 12
      ) {
        finalDate = `20${value.slice(-2)}-${value.slice(1, 3)}-0${value.slice(0, 1)}`;
        e.target.value = finalDate;
        setRawValue(formatDateStringToFrenchDate(e.target.value));
        onChange(e);
      } else if (
        (value.length === 6 || value.length === 8) &&
        value.slice(0, 2) <= 31 &&
        value.slice(2, 4) <= 12
      ) {
        finalDate = `20${value.slice(-2)}-${value.slice(2, 4)}-${value.slice(0, 2)}`;
        e.target.value = finalDate;
        setRawValue(formatDateStringToFrenchDate(e.target.value));
        onChange(e);
      } else if (isValidDate(value)) {
        finalDate = formatDateStringToDBDate(value);
      } else {
        setMessageBoxConfig({
          message: `Merci de renseigner une date au format DMM, DDMM, DMMYY, DDMMYY.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => {
            setShowMessageBox(false);
            e.target.focus();
          },
        });
        setShowMessageBox(true);
      }

      if (finalDate && disableFutureDates && isDateAfterToday(finalDate)) {
        setMessageBoxConfig({
          message: `La date ne peut pas être ultérieure à aujourd'hui.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => {
            setShowMessageBox(false);
            e.target.focus();
          },
        });
        setShowMessageBox(true);
        return;
      }

      if (
        finalDate &&
        !disablePastDateWarning &&
        isDateBeforeToday(finalDate) &&
        warnedValue !== finalDate
      ) {
        setWarnedValue(finalDate);
        showPastDateWarning();
      }
    }
  };

  return (
    <>
      <input
        type="text"
        name={name}
        placeholder={placeholder}
        value={rawValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      {showMessageBox && (
        <MessageBox
          message={messageBoxConfig.message}
          onClose={() => setShowMessageBox(false)}
          onConfirm={messageBoxConfig.onConfirm}
          onCancel={messageBoxConfig.onCancel}
          type={messageBoxConfig.type}
          loading_button={messageBoxConfig.loading_button}
          input={messageBoxConfig.input}
          technicalDetails={messageBoxConfig.technicalDetails}
        />
      )}
    </>
  );
}
